import 'swiper/css';
import 'swiper/css/scrollbar';
import 'swiper/css/navigation';

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { buttonVariants } from '@/components/v2/UI/Button';
import MyHead from '../../components/MyHead';
import Image from 'next/future/image';
import VideoPlayer from '@/components/v2/VideoPlayer';
import { GetServerSidePropsContext } from 'next';
import { scrollToId } from '@/helpers/scroller';
import LinkTo from '@/components/LinkTo';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Scrollbar } from 'swiper';

const StoresPage: React.FC = () => {
  const { t } = useTranslation();

  const cards = useMemo(
    () => [
      {
        title: t('P_STORES.SECTION-5.STORE-1.TITLE'),
        subtitle: t('P_STORES.SECTION-5.STORE-1.SUBTITLE'),
        address: t('P_STORES.SECTION-5.STORE-1.ADDRESS'),
        src: '/v2/stores/shops/asola.jpg'
      },
      {
        title: t('P_STORES.SECTION-5.STORE-2.TITLE'),
        subtitle: t('P_STORES.SECTION-5.STORE-2.SUBTITLE'),
        address: t('P_STORES.SECTION-5.STORE-2.ADDRESS'),
        src: '/v2/stores/shops/milano.jpg'
      },
      {
        title: t('P_STORES.SECTION-5.STORE-3.TITLE'),
        subtitle: t('P_STORES.SECTION-5.STORE-3.SUBTITLE'),
        address: t('P_STORES.SECTION-5.STORE-3.ADDRESS'),
        src: '/v2/stores/shops/brescia.jpg'
      },
      {
        title: t('P_STORES.SECTION-5.STORE-4.TITLE'),
        subtitle: t('P_STORES.SECTION-5.STORE-4.SUBTITLE'),
        address: t('P_STORES.SECTION-5.STORE-4.ADDRESS'),
        src: '/v2/stores/shops/trento.jpg'
      },
      {
        title: t('P_STORES.SECTION-5.STORE-5.TITLE'),
        subtitle: t('P_STORES.SECTION-5.STORE-5.SUBTITLE'),
        address: t('P_STORES.SECTION-5.STORE-5.ADDRESS'),
        src: '/v2/stores/shops/peschiera.jpg'
      },
      {
        title: t('P_STORES.SECTION-5.STORE-6.TITLE'),
        subtitle: t('P_STORES.SECTION-5.STORE-6.SUBTITLE'),
        address: t('P_STORES.SECTION-5.STORE-6.ADDRESS'),
        src: '/v2/stores/shops/viareggio.jpg'
      },
      {
        title: t('P_STORES.SECTION-5.STORE-7.TITLE'),
        subtitle: t('P_STORES.SECTION-5.STORE-7.SUBTITLE'),
        address: t('P_STORES.SECTION-5.STORE-7.ADDRESS'),
        src: '/v2/stores/shops/salo.jpg'
      },
      {
        title: t('P_STORES.SECTION-5.STORE-8.TITLE'),
        subtitle: t('P_STORES.SECTION-5.STORE-8.SUBTITLE'),
        address: t('P_STORES.SECTION-5.STORE-8.ADDRESS'),
        src: '/v2/stores/shops/madonna.jpg'
      },
      {
        title: t('P_STORES.SECTION-5.STORE-9.TITLE'),
        subtitle: t('P_STORES.SECTION-5.STORE-9.SUBTITLE'),
        address: t('P_STORES.SECTION-5.STORE-9.ADDRESS'),
        src: '/v2/stores/shops/torbole.jpg'
      },
      {
        title: t('P_STORES.SECTION-5.STORE-10.TITLE'),
        subtitle: t('P_STORES.SECTION-5.STORE-10.SUBTITLE'),
        address: t('P_STORES.SECTION-5.STORE-10.ADDRESS'),
        src: '/v2/stores/shops/roma.jpg'
      },
      {
        title: t('P_STORES.SECTION-5.STORE-11.TITLE'),
        subtitle: t('P_STORES.SECTION-5.STORE-11.SUBTITLE'),
        address: t('P_STORES.SECTION-5.STORE-11.ADDRESS'),
        src: '/v2/stores/shops/sanginesio.jpg'
      },
      {
        title: t('P_STORES.SECTION-5.STORE-12.TITLE'),
        subtitle: t('P_STORES.SECTION-5.STORE-12.SUBTITLE'),
        address: t('P_STORES.SECTION-5.STORE-12.ADDRESS'),
        src: '/v2/stores/shops/germania.jpg'
      },
      {
        title: t('P_STORES.SECTION-5.STORE-13.TITLE'),
        subtitle: t('P_STORES.SECTION-5.STORE-13.SUBTITLE'),
        address: t('P_STORES.SECTION-5.STORE-13.ADDRESS'),
        src: '/v2/stores/shops/padova.jpg'
      }
    ],
    []
  );

  return (
    <div>
      <MyHead />

      <div className="section relative full bigspace">
        <Image
          src="/v2/stores/store-header.jpg"
          alt="Store preview"
          width={0}
          height={0}
          sizes="100vw"
          priority
          className="w-full absolute top-0 left-0 h-full object-cover object-center"
        />
        <div className="section-wrapper relative text-white">
          <div className="grid gap-8 grid-cols-12 md:gap-12 xl:gap-20">
            <div className="md:col-span-8 col-span-12">
              <div className="h2">{t('P_STORES.SECTION-1.TITLE')}</div>
              <div className="tracking-uyn uppercase md:text-3xl opacity-80 mb-2">
                {t('P_STORES.SECTION-1.DESC')}
              </div>
              <div className="flex gap-2 flex-wrap mt-6">
                <a
                  className={buttonVariants({ variant: 'light' })}
                  onClick={() => scrollToId('store-map')}
                >
                  {t('P_STORES.SECTION-1.BTN')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="section relative bg-sand-100 text-sand-500 full">
        <div className="section-wrapper relative">
          <div className="grid gap-8 grid-cols-12">
            <div className="col-span-12 text-center mb-10">
              <div className="h1">{t('P_STORES.SECTION-2.LEAD')}</div>
            </div>

            <div className="md:col-span-5 col-span-12 space-y-16 self-center">
              <div>
                <div className="h3">{t('P_STORES.SECTION-2.TITLE')}</div>
                <div className="text-body mt-6 text-black">
                  {t('P_STORES.SECTION-2.LINE-1')}
                </div>
                <div className="text-body mt-3 text-black">
                  {t('P_STORES.SECTION-2.LINE-2')}
                </div>
                {/* <div className="text-body mt-3 text-black">
                  {t('P_STORES.SECTION-2.LINE-3')}
                </div> */}
              </div>
            </div>

            <div className="md:col-span-6 md:col-start-7  col-span-12 relative h-fit">
              <Image
                src="/v2/stores/hor1.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full max-w-[90%] ml-auto relative z-10"
              />
              <Image
                src="/v2/stores/ver1.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full max-w-[50%] mt-[-25%] mr-auto relative z-20"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="section relative bg-sand-100 full">
        <div className="video-wrapper aspect-landscape w-full">
          <VideoPlayer src="https://vimeo.com/847890162" autoPlay />
        </div>
      </div>

      <div className="section relative bg-sand-100 text-sand-500 full">
        <div className="section-wrapper relative">
          <div className="grid gap-8 grid-cols-12">
            <div className="md:col-span-6 col-span-12 relative h-fit">
              <Image
                src="/v2/stores/hor2.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full max-w-[90%] mr-auto relative z-10"
              />
              <Image
                src="/v2/stores/ver2.jpg"
                alt=""
                width={0}
                height={0}
                sizes="100vw"
                className="w-full max-w-[50%] mt-[-25%] ml-auto relative z-20"
              />
            </div>
            <div className="md:col-span-5 md:col-start-8 col-span-12 space-y-16 self-center">
              <div>
                <div className="h3">{t('P_STORES.SECTION-4.TITLE')}</div>
                <div className="text-body mt-6 text-black">
                  {t('P_STORES.SECTION-4.LINE-1')}
                </div>
                <div className="flex gap-2 flex-wrap mt-6">
                  <LinkTo
                    href="/materials"
                    className={buttonVariants({ variant: 'dark' })}
                  >
                    {t('P_STORES.SECTION-4.BTN')}
                  </LinkTo>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="section relative full bg-gray-50">
        <div className="section-wrapper relative">
          <div className="text-center mb-10">
            <div className="h3">{t('P_STORES.SECTION-5.TITLE')}</div>
          </div>
          <div className="relative">
            <Swiper
              wrapperTag="ul"
              slidesPerView={1.2}
              spaceBetween={16}
              navigation={true}
              modules={[Scrollbar, Navigation]}
              scrollbar={{ hide: false }}
              className="swiperScrollbar"
              breakpoints={{
                768: {
                  slidesPerView: 2.2
                },
                1024: {
                  slidesPerView: 4
                }
              }}
            >
              {cards.map((card, idx) => (
                <SwiperSlide key={idx} tag="li" className="">
                  <Image
                    src={card.src}
                    alt=""
                    width={0}
                    height={0}
                    sizes="100vw"
                    className="w-full h-auto rounded-uyn"
                  />

                  <div className="pt-6 pr-3">
                    <h3 className="h5">{card.title}</h3>
                    <p className="font-bold mt-2">{card.subtitle}</p>
                    <p className="italic text-sm max-w-[85%]">{card.address}</p>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </div>

      <div className="section relative full" id="store-map">
        <div className="section-wrapper relative">
          <div className="text-center mb-10">
            <div className="h3">{t('P_STORES.SECTION-6.TITLE')}</div>
            <div className="text-body">{t('P_STORES.SECTION-6.SUBTITLE')}</div>
          </div>

          <iframe
            allow="geolocation"
            src="https://blog.uynsports.com/flagship-stores-locator/"
            className="w-full min-h-80vh font-safe"
          />
        </div>
      </div>
    </div>
  );
};

export default StoresPage;
